import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { FiX, FiAward, FiCalendar, FiClock, FiUser, FiPlus, FiRefreshCw } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { fmtDate } from '../../utils/istDate';

const api = axios.create({ baseURL: process.env.REACT_APP_BASE_URL });
api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('authToken');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const initials = (name = '') =>
  name.split(' ').map(w => w[0] || '').join('').slice(0, 2).toUpperCase() || '??';

export default function EarnedLeaveDetailsModal({
  isOpen,
  onClose,
  employee,
  onCreditEarnedLeave,
}) {
  const [loading, setLoading] = useState(true);
  const [earnedRecords, setEarnedRecords] = useState([]);
  const [balance, setBalance] = useState(null);

  const empId = employee?.employeeId;
  const empName = employee?.fullName || employee?.name || employee?.username || `Employee ${empId}`;

  const fetchRecords = async () => {
    if (!empId) return;
    setLoading(true);
    try {
      const curYear = new Date().getFullYear();
      const [elRes, balRes] = await Promise.allSettled([
        api.get(`/leave/subordinate/${empId}/earned-leaves`),
        api.get(`/leave/subordinate/${empId}/monthly-balance/${curYear}`),
      ]);

      if (elRes.status === 'fulfilled') {
        const raw = elRes.value.data?.data ?? elRes.value.data ?? [];
        setEarnedRecords(Array.isArray(raw) ? raw : []);
      }
      if (balRes.status === 'fulfilled') {
        setBalance(balRes.value.data?.data ?? balRes.value.data);
      }
    } catch (err) {
      console.error('Failed to load earned leave details:', err);
      toast.error('Could not load earned leave details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && empId) {
      fetchRecords();
    }
  }, [isOpen, empId]);

  if (!isOpen || !employee) return null;

  const totalEarnedDays = balance?.earnedLeave ?? employee?.earnedLeave ?? (
    earnedRecords.reduce((acc, r) => acc + (r.creditDays || 0), 0)
  );

  const modalNode = (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 bg-slate-900 text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center font-bold text-sm text-sky-400">
              {initials(empName)}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-white text-base">{empName}</h3>
                <span className="text-xs font-mono px-2 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700">
                  #{empId}
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                Earned Leaves Credit History &amp; Details
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onCreditEarnedLeave && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onCreditEarnedLeave(employee);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 transition-colors cursor-pointer shadow-xs"
              >
                <FiPlus size={13} /> Credit Earned
              </button>
            )}
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-800 transition-colors cursor-pointer"
            >
              <FiX size={18} />
            </button>
          </div>
        </div>

        {/* Content Body */}
        <div className="p-6 overflow-y-auto space-y-5 bg-slate-50/50 flex-1">
          {/* Summary Box */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">
                Earned Leave Balance
              </span>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-black text-slate-900 leading-none">
                  {totalEarnedDays}
                </span>
                <span className="text-xs font-semibold text-slate-500">
                  day{totalEarnedDays === 1 ? '' : 's'}
                </span>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">
                Credit Events
              </span>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-black text-slate-900 leading-none">
                  {earnedRecords.length}
                </span>
                <span className="text-xs font-semibold text-slate-500">
                  record{earnedRecords.length === 1 ? '' : 's'}
                </span>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs col-span-2 sm:col-span-1">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">
                Policy Rule
              </span>
              <span className="text-xs text-slate-600 leading-tight block">
                Credits added directly to annual quota pool
              </span>
            </div>
          </div>

          {/* Credits Ledger Table */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60 flex items-center justify-between">
              <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                <FiAward size={14} className="text-slate-500" /> Credit History
              </span>
              <button
                type="button"
                onClick={fetchRecords}
                disabled={loading}
                className="text-xs font-medium text-slate-500 hover:text-slate-800 flex items-center gap-1 cursor-pointer"
              >
                <FiRefreshCw size={12} className={loading ? 'animate-spin' : ''} />
                <span>Refresh</span>
              </button>
            </div>

            {loading ? (
              <div className="py-12 text-center text-slate-400">
                <FiRefreshCw className="animate-spin mx-auto mb-2" size={20} />
                <p className="text-xs font-medium">Loading credit records...</p>
              </div>
            ) : earnedRecords.length === 0 ? (
              <div className="py-10 text-center text-slate-400 px-4">
                <FiAward className="mx-auto mb-2 text-slate-300" size={28} />
                <p className="text-xs font-bold text-slate-700">No Earned Leave Credits Logged</p>
                <p className="text-[11px] text-slate-400 mt-1">
                  When this subordinate completes approved weekend or holiday duty, click "Credit Earned" above to grant leave days.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {earnedRecords.map((r) => (
                  <div key={r.id} className="p-4 hover:bg-slate-50/50 transition-colors flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-slate-900 flex items-center gap-1">
                          <FiCalendar size={13} className="text-slate-400" /> Duty Date: {fmtDate(r.effectiveDate)}
                        </span>
                        <span className="px-2 py-0.5 rounded-full font-bold bg-slate-100 text-slate-800 border border-slate-200 text-[10px]">
                          {r.creditDays} day{r.creditDays > 1 ? 's' : ''}
                        </span>
                      </div>
                      <p className="text-slate-600 text-[11px] leading-relaxed">
                        <strong className="text-slate-700">Reason:</strong> {r.remarks || 'Weekend or holiday shift completed'}
                      </p>
                    </div>

                    <div className="text-left sm:text-right text-[10px] text-slate-400 shrink-0">
                      <p className="flex items-center sm:justify-end gap-1">
                        <FiUser size={11} className="text-slate-400" />
                        <span>Credited by: <strong className="text-slate-600 font-semibold">{r.createdBy || 'Manager'}</strong></span>
                      </p>
                      {r.createdAt && (
                        <p className="flex items-center sm:justify-end gap-1 mt-0.5">
                          <FiClock size={11} className="text-slate-400" />
                          <span>Logged on: {fmtDate(r.createdAt)}</span>
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3.5 bg-slate-50 border-t border-slate-100 flex items-center justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 hover:text-slate-900 hover:bg-slate-200/60 border border-slate-200 transition-colors cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modalNode, document.body) : modalNode;
}
